import { useCallback } from 'react';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface PeriodColumn {
  date: Date;
  label: string;
  key: string;
}

interface Level1Data {
  id: string;
  code: string;
  name: string;
}

interface Level2Data {
  id: string;
  level1Id: string;
  code: string;
  name: string;
  type: 'receita' | 'despesa';
}

interface Level3Data {
  id: string;
  level2Id?: string;
  code?: string;
  name: string;
  type: 'receita' | 'despesa';
}

interface TransactionData {
  id: string;
  description: string;
  value: number;
  type: string;
  issueDate?: string;
  paymentDate?: string;
  categoryId: string;
  settled?: boolean;
  supplierId?: string;
}

interface HierarchyData {
  level3TotalsByMonth: Record<string, Record<string, { value: number; transactions: TransactionData[] }>>;
  level2TotalsByMonth: Record<string, Record<string, number>>;
  level1TotalsByMonth: Record<string, Record<string, number>>;
  grandTotalsByMonth: Record<string, number>;
  grandTotal: { receita: number; despesa: number };
}

interface ExportParams {
  level1Accounts: Level1Data[];
  level2Accounts: Level2Data[];
  level3Accounts: Level3Data[];
  periodColumns: PeriodColumn[];
  hierarchyData: HierarchyData;
  suppliers: { id: string; name: string }[];
  startDate: string;
  endDate: string;
}

const currency = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

export function useHierarchyExport() {
  const getSupplierName = (suppliers: { id: string; name: string }[], supplierId?: string) => {
    if (!supplierId) return '-';
    const supplier = suppliers.find(s => s.id === supplierId);
    return supplier?.name || '-';
  };

  const buildExportData = (params: ExportParams) => {
    const {
      level1Accounts,
      level2Accounts,
      level3Accounts,
      periodColumns,
      hierarchyData,
      suppliers,
    } = params;

    const rows: { indent: number; name: string; values: (string | number)[]; isTotal?: boolean }[] = [];
    const sortedL1 = [...level1Accounts].sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));

    sortedL1.forEach(l1 => {
      const l1MonthData = hierarchyData.level1TotalsByMonth[l1.id] || {};
      const l1Total = periodColumns.reduce((sum, p) => sum + (l1MonthData[p.key] || 0), 0);
      
      // Skip if no data
      if (!periodColumns.some(p => l1MonthData[p.key] !== 0)) return;

      // Level 1 row
      rows.push({
        indent: 0,
        name: `${l1.code} - ${l1.name}`,
        values: [...periodColumns.map(p => l1MonthData[p.key] || 0), l1Total],
      });

      // Level 2 children
      const childL2 = level2Accounts.filter(l2 => l2.level1Id === l1.id);
      childL2.forEach(l2 => {
        const l2MonthData = hierarchyData.level2TotalsByMonth[l2.id] || {};
        const l2Total = periodColumns.reduce((sum, p) => sum + (l2MonthData[p.key] || 0), 0);
        
        const childL3 = level3Accounts.filter(l3 => l3.level2Id === l2.id);
        const hasL3Data = childL3.some(l3 => {
          const l3Data = hierarchyData.level3TotalsByMonth[l3.id] || {};
          return periodColumns.some(p => (l3Data[p.key]?.transactions?.length || 0) > 0);
        });

        if (!hasL3Data && !periodColumns.some(p => l2MonthData[p.key] !== 0)) return;

        // Level 2 row
        rows.push({
          indent: 1,
          name: `${l2.code} - ${l2.name}`,
          values: [...periodColumns.map(p => l2MonthData[p.key] || 0), l2Total],
        });

        // Level 3 children
        childL3.forEach(l3 => {
          const l3MonthData = hierarchyData.level3TotalsByMonth[l3.id] || {};
          const l3Total = periodColumns.reduce((sum, p) => sum + (l3MonthData[p.key]?.value || 0), 0);
          
          const hasTx = periodColumns.some(p => (l3MonthData[p.key]?.transactions?.length || 0) > 0);
          if (!hasTx) return;

          // Level 3 row
          rows.push({
            indent: 2,
            name: `${l3.code} - ${l3.name}`,
            values: [...periodColumns.map(p => l3MonthData[p.key]?.value || 0), l3Total],
          });

          // Supplier breakdown
          const supplierTotals: Record<string, Record<string, number>> = {};
          periodColumns.forEach(period => {
            const txs = l3MonthData[period.key]?.transactions || [];
            txs.forEach(tx => {
              const supplierId = tx.supplierId || 'sem-fornecedor';
              if (!supplierTotals[supplierId]) supplierTotals[supplierId] = {};
              if (!supplierTotals[supplierId][period.key]) supplierTotals[supplierId][period.key] = 0;
              supplierTotals[supplierId][period.key] += tx.type === 'receita' ? tx.value : -tx.value;
            });
          });

          Object.entries(supplierTotals).forEach(([supplierId, months]) => {
            const supplierTotal = periodColumns.reduce((sum, p) => sum + (months[p.key] || 0), 0);
            rows.push({
              indent: 3,
              name: supplierId === 'sem-fornecedor' ? '-' : getSupplierName(suppliers, supplierId),
              values: [...periodColumns.map(p => months[p.key] || 0), supplierTotal],
            });
          });
        });
      });
    });

    // RESULTADO row
    const grandTotalValue = hierarchyData.grandTotal.receita - hierarchyData.grandTotal.despesa;
    rows.push({
      indent: 0,
      name: 'RESULTADO',
      values: [...periodColumns.map(p => hierarchyData.grandTotalsByMonth[p.key] || 0), grandTotalValue],
      isTotal: true,
    });

    return rows;
  };

  const exportToExcel = useCallback(async (params: ExportParams) => {
    const { periodColumns, startDate, endDate } = params;
    const rows = buildExportData(params);

    // Build worksheet data
    const headers = ['Conta / Fornecedor', ...periodColumns.map(p => p.label), 'Total'];

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Análise Plano de Contas');

    // Add header row
    worksheet.addRow(headers);

    // Style header row
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF646464' }
    };
    headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };

    // Add data rows
    rows.forEach(row => {
      const indent = '  '.repeat(row.indent);
      const formattedValues = row.values.map(v => 
        typeof v === 'number' ? currency.format(v) : v
      );
      worksheet.addRow([`${indent}${row.name}`, ...formattedValues]);
    });

    // Set column widths
    worksheet.columns = [
      { width: 50 },
      ...periodColumns.map(() => ({ width: 15 })),
      { width: 18 },
    ];

    // Generate file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);

    const filename = `analise-plano-contas_${startDate}_${endDate}.xlsx`;
    
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    
    URL.revokeObjectURL(url);
  }, []);

  const exportToPDF = useCallback((params: ExportParams) => {
    const { periodColumns, startDate, endDate, hierarchyData } = params;
    const rows = buildExportData(params);

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
    });

    // Title
    doc.setFontSize(16);
    doc.text('Análise do Plano de Contas', 14, 15);
    doc.setFontSize(10);
    doc.text(`Período: ${startDate} a ${endDate}`, 14, 22);

    // Summary
    doc.setFontSize(9);
    doc.text(`Receitas: ${currency.format(hierarchyData.grandTotal.receita)}`, 14, 30);
    doc.text(`Despesas: ${currency.format(hierarchyData.grandTotal.despesa)}`, 80, 30);
    doc.text(`Resultado: ${currency.format(hierarchyData.grandTotal.receita - hierarchyData.grandTotal.despesa)}`, 150, 30);

    // Table
    const headers = ['Conta / Fornecedor', ...periodColumns.map(p => p.label), 'Total'];
    const tableData = rows.map(row => {
      const indent = '  '.repeat(row.indent);
      const formattedValues = row.values.map(v => 
        typeof v === 'number' ? currency.format(v) : String(v)
      );
      return [`${indent}${row.name}`, ...formattedValues];
    });

    autoTable(doc, {
      head: [headers],
      body: tableData,
      startY: 35,
      styles: {
        fontSize: 7,
        cellPadding: 1.5,
      },
      headStyles: {
        fillColor: [100, 100, 100],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
      columnStyles: {
        0: { cellWidth: 60 },
      },
      didParseCell: (data) => {
        // Style RESULTADO row
        if (data.row.index === tableData.length - 1) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [230, 230, 250];
        }
      },
    });

    const filename = `analise-plano-contas_${startDate}_${endDate}.pdf`;
    doc.save(filename);
  }, []);

  return { exportToExcel, exportToPDF };
}
